import { useEffect, useRef, useState } from "react";
import { useAuthProviders } from "src/auth/contexts/AuthContext";
import { useIntl } from "react-intl";
import logError from "src/utils/logError";

type Props = {
  onClick: () => void;
};

function onGoogleSigninLoad(
  parentElement: HTMLDivElement,
  {
    locale,
    clickListener,
  }: {
    locale: string;
    clickListener: VoidFunction;
  },
) {
  try {
    google.accounts.id.renderButton(parentElement, {
      type: "standard",
      shape: "rectangular",
      theme: "outline",
      text: "continue_with",
      size: "large",
      locale,
      logo_alignment: "center",
      click_listener: clickListener,
      // 'width' property defines the minimum button width, in pixels.
      // Since google api doesn't let us set the button width more than 400px or 100%,
      // This is a workaround to make the button responsive.
      // More info in https://stackoverflow.com/a/69552808/15975629
      width: parentElement.offsetWidth,
    });
  } catch (error) {
    logError(error, ["user-accounts"]);
  }
}

export function GoogleSignIn(props: Props) {
  const googleSignInParentElement = useRef<HTMLDivElement>(null);
  const { googleLoading } = useAuthProviders();
  const [hasBeenInitialized, setHasBeenInitialized] = useState(false);
  const intl = useIntl();

  useEffect(() => {
    if (
      googleSignInParentElement.current &&
      !hasBeenInitialized &&
      !googleLoading
    ) {
      onGoogleSigninLoad(googleSignInParentElement.current, {
        locale: intl.locale,
        clickListener: props.onClick,
      });
      setHasBeenInitialized(true);
    }
  }, [
    props.onClick,
    intl.locale,
    googleSignInParentElement,
    hasBeenInitialized,
    googleLoading,
  ]);

  return (
    <div
      data-testid="google-signin-button-parent"
      // There is a unexpected margin from google sign in button that causes the button to be moved up by a negative margin.
      className="h-11 w-full [&>iframe]:!my-0"
      ref={googleSignInParentElement}
    ></div>
  );
}
